import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Car, LogIn, UserPlus, ArrowLeft } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientAuth = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("login");
  const [loading, setLoading] = useState(false);
  
  const [loginData, setLoginData] = useState({
    email: "",
    password: ""
  });
  
  const [registerData, setRegisterData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    nom: "",
    prenom: "",
    telephone: ""
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!loginData.email || !loginData.password) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/client/login`, {
        email: loginData.email,
        password: loginData.password
      });
      
      localStorage.setItem('client_token', response.data.token);
      localStorage.setItem('client_data', JSON.stringify(response.data.client));
      
      toast.success("Connexion réussie");
      navigate('/espace-client');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Identifiants invalides");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!registerData.email || !registerData.password || !registerData.nom || !registerData.prenom) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    
    if (registerData.password !== registerData.confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    
    if (registerData.password.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/client/register`, {
        email: registerData.email,
        password: registerData.password,
        nom: registerData.nom,
        prenom: registerData.prenom,
        telephone: registerData.telephone
      });
      
      localStorage.setItem('client_token', response.data.token);
      localStorage.setItem('client_data', JSON.stringify(response.data.client));
      
      toast.success("Compte créé avec succès !");
      navigate('/espace-client');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#000091] rounded-lg flex items-center justify-center">
                <Car className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-[#000091]">Carte Grise Étrangère</h1>
                <p className="text-xs text-slate-500">Espace Client</p>
              </div>
            </div>
            <Link to="/" className="text-sm text-slate-600 hover:text-[#000091] flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" />
              Retour au site
            </Link>
          </div>
        </div>
        <div className="h-1 flex">
          <div className="flex-1 bg-[#002395]"></div>
          <div className="flex-1 bg-white"></div>
          <div className="flex-1 bg-[#ED2939]"></div>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-12">
        <Card className="border-slate-200" data-testid="client-auth-card">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-[#0F172A]">Mon espace</CardTitle>
            <CardDescription>
              Connectez-vous ou créez un compte pour suivre vos dossiers
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login" className="data-[state=active]:bg-[#000091] data-[state=active]:text-white">
                  <LogIn className="w-4 h-4 mr-2" />
                  Se connecter
                </TabsTrigger>
                <TabsTrigger value="register" className="data-[state=active]:bg-[#000091] data-[state=active]:text-white">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Inscription
                </TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      value={loginData.email}
                      onChange={(e) => setLoginData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="votre@email.com"
                      data-testid="login-email-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Mot de passe</Label>
                    <Input
                      id="login-password"
                      type="password"
                      value={loginData.password}
                      onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                      placeholder="••••••••"
                      data-testid="login-password-input"
                    />
                  </div>

                  <Button 
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#000091] hover:bg-[#000091]/90"
                    data-testid="login-submit-btn"
                  >
                    {loading ? "Connexion..." : "Se connecter"}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-prenom">Prénom *</Label>
                      <Input
                        id="register-prenom"
                        value={registerData.prenom}
                        onChange={(e) => setRegisterData(prev => ({ ...prev, prenom: e.target.value }))}
                        placeholder="Jean"
                        data-testid="register-prenom-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-nom">Nom *</Label>
                      <Input
                        id="register-nom"
                        value={registerData.nom}
                        onChange={(e) => setRegisterData(prev => ({ ...prev, nom: e.target.value }))}
                        placeholder="DUPONT"
                        data-testid="register-nom-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email *</Label>
                    <Input
                      id="register-email"
                      type="email"
                      value={registerData.email}
                      onChange={(e) => setRegisterData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="votre@email.com"
                      data-testid="register-email-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-telephone">Téléphone</Label>
                    <Input
                      id="register-telephone"
                      type="tel"
                      value={registerData.telephone}
                      onChange={(e) => setRegisterData(prev => ({ ...prev, telephone: e.target.value }))}
                      placeholder="06 12 34 56 78"
                      data-testid="register-telephone-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="register-password">Mot de passe *</Label>
                    <Input
                      id="register-password"
                      type="password"
                      value={registerData.password}
                      onChange={(e) => setRegisterData(prev => ({ ...prev, password: e.target.value }))}
                      placeholder="••••••••"
                      data-testid="register-password-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-confirm-password">Confirmer le mot de passe *</Label>
                    <Input
                      id="register-confirm-password"
                      type="password"
                      value={registerData.confirmPassword}
                      onChange={(e) => setRegisterData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      placeholder="••••••••"
                      data-testid="register-confirm-password-input"
                    />
                  </div>

                  <Button 
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#000091] hover:bg-[#000091]/90"
                    data-testid="register-submit-btn"
                  >
                    {loading ? "Création..." : "Créer mon compte"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ClientAuth;
